import { useEffect, useState } from 'react';
import {
  DefaultGenerics,
  ExtendableGenerics,
  StreamChat,
  TokenOrProvider,
  User,
} from 'stream-chat';
import { STREAM_CHAT_ENABLED } from '@/config';

export type UseClientOptions = {
  apiKey?: string;
  enabled?: boolean;
  user?: User;
  tokenOrProvider?: TokenOrProvider;
};

export const useChatClient = <
  SCG extends ExtendableGenerics = DefaultGenerics,
>({
  apiKey,
  enabled = STREAM_CHAT_ENABLED,
  user,
  tokenOrProvider,
}: UseClientOptions) => {
  const [chatClient, setChatClient] = useState<StreamChat<SCG> | null>(null);
  useEffect(() => {
    if (!enabled || !apiKey || !user?.id || !tokenOrProvider) {
      setChatClient(null);
      return undefined;
    }

    const client = new StreamChat<SCG>(apiKey);
    // prevents application from setting stale client (user changed, for example)
    let didUserConnectInterrupt = false;

    const connectionPromise = client
      .connectUser(user, tokenOrProvider)
      .then(() => {
        if (!didUserConnectInterrupt) {
          setChatClient(client);
        }
      })
      .catch((e) => {
        if (!didUserConnectInterrupt) {
          setChatClient(null);
        }
        console.error(`Failed to connect user`, e);
      });

    return () => {
      didUserConnectInterrupt = true;
      connectionPromise.then(() => {
        setChatClient(null);
        client.disconnectUser().catch((e) => {
          console.error(`Failed to disconnect user`, e);
        });
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should re-run only if user.id changes
  }, [apiKey, enabled, user?.id, tokenOrProvider]);

  return chatClient;
};
